/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { API } from "aws-amplify";
import { sanitizeInvoice } from "../../../graphql/invoice/invoiceCRUD";

import { endOfDay } from "date-fns";

import {
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  Tooltip,
} from "@material-ui/core";

import { SaveButton, useNotify, useRedirect } from "react-admin";
import { useForm, useFormState } from "react-final-form";

import {
  ArrowDropDownIcon,
  ImportExportIcon,
  SaveIcon,
  UploadIcon,
} from "../../../components/common/iconography";

import { logError, logHistory } from "../../../utils/appUtils";
import { isUserFacingError } from "../../../utils/lambdaErrors";
import {
  ExternalStatus,
  FilePaths,
  InvoiceStatus,
  NoteType,
  ResourceType,
  SORTKEY_BUFFER,
} from "../../../utils/constants";
import { removeFileKeys, saveRemoveFiles } from "../../../utils/fileUtils";
import {
  autogenHistoryNotes,
  deepRedirect,
  trimDecimal,
} from "../../../utils/utils";
import {
  checkDirtySync,
  addServiceNames,
  sanitizeZapierData,
  triggerEvent,
} from "../../../utils/zapierUtils";

import {
  createInvoiceServiceLambda,
  setInvoiceSyncFailed,
  setInvoiceSyncPending,
} from "./InvoiceUtils";
import { syncableInvoiceUpdateProps } from "../../../graphql/invoice/invoiceCRUD";

const STORAGE_KEY = "invoices:SaveButtonMode";

export const SplitSaveButton = ({
  label,
  record,
  backPath,
  isSaving,
  setIsSaving,
  isDeleting,
  isSyncEnabled,
  isLocked,
  ...props
}) => {
  const form = useForm();
  const { values: formValues, pristine, dirtyFields } = useFormState();
  const computedFields = new Set([
    "lineItems.items",
    "subtotal",
    "tax",
    "total",
    "balance",
  ]);
  const notify = useNotify();
  const history = useHistory();
  const redirect = useRedirect();
  const capturedRef = useRef({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [mode, setMode] = useState("save"); // 'save' | 'save_send'

  const isSyncDirty = useMemo(() => {
    if (!isSyncEnabled) return false;
    return checkDirtySync(dirtyFields, syncableInvoiceUpdateProps);
  }, [dirtyFields, isSyncEnabled]);

  // Load mode from localStorage on component mount
  useEffect(() => {
    const savedMode = localStorage.getItem(STORAGE_KEY);
    if (savedMode && (savedMode === "save" || savedMode === "save_send")) {
      setMode(savedMode);
    }
  }, []);

  // Save mode to localStorage whenever it changes
  const handleModeChange = (newMode) => {
    setMode(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
  };

  const transform = async (data) => {
    setIsSaving(true);
    data = { ...data };
    capturedRef.current.lineItems = data.lineItems;

    data.dueDate = endOfDay(new Date(data.dueDate));

    // The invoice write can still fail after this (a conflict, or the invoice
    // voided meanwhile), so nothing permanent happens here: history notes are
    // logged and removed attachments deleted in onSuccess, once the write
    // has gone through.
    const ignoreList = ["lineItems", "lineItems.items", "paymentTermId"];
    const includeArrayList = ["customFields"];
    capturedRef.current.historyNotes = autogenHistoryNotes(
      form,
      data.id,
      ResourceType.INVOICE,
      ignoreList,
      includeArrayList,
      [],
      { log: false },
    );

    data.contact = data.contact === "" ? null : data.contact;

    capturedRef.current.files = null;
    try {
      capturedRef.current.files = await saveRemoveFiles(
        record,
        data,
        "attachments",
        `${FilePaths.INVOICE}/${Date.now()}`,
        { deferDelete: true },
      );
    } catch (error) {
      logError("invoices:InvoiceEdit:transform", error, notify);
    }

    if (isSyncEnabled && isSyncDirty) {
      data.externalStatus = ExternalStatus.SYNC_PENDING;
    }

    // Status, paid date, overdue, balance and active move outside this form
    // (Record Payment, Complete, Reopen, Void). The data provider takes them
    // from the invoice as stored at write time and writes under an updatedAt
    // check, so a page left open, or a payment landing while this saves,
    // can't be undone by this save. See saveInvoiceForm.
    const sanitized = sanitizeInvoice(data);
    return { ...sanitized, __saveContext: true };
  };

  const onSuccess = async ({ data }) => {
    // The invoice is written: now log its history and delete the attachments
    // it no longer lists. Both are fire-and-forget.
    (capturedRef.current.historyNotes || []).forEach((note) =>
      logHistory(note),
    );
    removeFileKeys(capturedRef.current.files?.removedKeys);

    try {
      const originalItems = record?.lineItems?.items ?? [];
      const currentItems = capturedRef.current.lineItems?.items ?? [];

      const invoiceServiceCreateList = [];
      const invoiceServiceUpdateList = [];
      const invoiceServiceDeleteList = [];
      const inboundUpdateMap = {};
      const outboundUpdateMap = {};
      const seenIds = new Set();
      // Track remaining shipment links after edits so we only clear invoiceId
      // when *all* line items for a shipment were removed.
      const remainingInboundIds = new Set(
        currentItems
          .map((item) => item?.inboundShipmentId)
          .filter((id) => Boolean(id)),
      );
      const remainingOutboundIds = new Set(
        currentItems
          .map((item) => item?.outboundShipmentId)
          .filter((id) => Boolean(id)),
      );

      currentItems.forEach((lineItem, idx) => {
        if (lineItem.id) {
          // UPDATE LineItem
          seenIds.add(lineItem.id);
          const amount = Number(lineItem?.amount ?? 0);
          const itemTax = Number(lineItem?.itemTax ?? 0);

          const invoiceServiceUpdateInput = {
            ...lineItem,
            event: data.event,
            amount: trimDecimal(amount, 2),
            itemTax: trimDecimal(itemTax, 2),
            itemTotal: trimDecimal(amount + itemTax, 2),
            updatedAt: data.updatedAt,
            sortKey: idx + SORTKEY_BUFFER,
          };

          invoiceServiceUpdateList.push({ ...invoiceServiceUpdateInput });
        } else {
          // !lineItem.id
          // CREATE LineItem
          const li = createInvoiceServiceLambda(data, lineItem, idx);
          invoiceServiceCreateList.push(li);
        }
      });

      // DELETE LineItem
      for (const origLineItem of originalItems) {
        if (!seenIds.has(origLineItem.id)) {
          invoiceServiceDeleteList.push({ id: origLineItem.id });

          const historyNote = {
            text: `Removed from Invoice`,
            by: window.appUser?.fullName,
            date: data.updatedAt,
            type: NoteType.GENERIC,
          };

          // Map shipment updates for clearing invoiceId only if this was the
          // last line item referencing the shipment (bulk invoices).
          if (origLineItem.inboundShipmentId) {
            const shouldClearInbound =
              !data.inboundShipmentId &&
              !remainingInboundIds.has(origLineItem.inboundShipmentId);
            const nextInboundInvoiceId = shouldClearInbound
              ? " " // required for secondary index
              : origLineItem.invoiceId;
            inboundUpdateMap[origLineItem.id] = {
              id: origLineItem.inboundShipmentId,
              invoiceId: nextInboundInvoiceId,
              history: [...origLineItem.inboundShipment?.history, historyNote],
              updatedAt: data.updatedAt,
            };
          }
          if (origLineItem.outboundShipmentId) {
            const shouldClearOutbound =
              !data.outboundShipmentId &&
              !remainingOutboundIds.has(origLineItem.outboundShipmentId);
            const nextOutboundInvoiceId = shouldClearOutbound
              ? " " // required for secondary index
              : origLineItem.invoiceId;
            outboundUpdateMap[origLineItem.id] = {
              id: origLineItem.outboundShipmentId,
              invoiceId: nextOutboundInvoiceId,
              history: [...origLineItem.outboundShipment?.history, historyNote],
              updatedAt: data.updatedAt,
            };
          }
        }
      }

      if (
        invoiceServiceCreateList.length > 0 ||
        invoiceServiceUpdateList.length > 0 ||
        invoiceServiceDeleteList.length > 0
      ) {
        await API.post("transREST", "/invoiceUpdate", {
          body: {
            invoiceServiceCreateList,
            invoiceServiceUpdateList,
            invoiceServiceDeleteList,
            inboundUpdateMap,
            outboundUpdateMap,
            user: window.appUser.email,
            reloadYardId: window.appUser.reloadYardId,
          },
        });
      }

      if (isSyncDirty) {
        const zapierData = {
          ...data,
          lineItems: capturedRef.current.lineItems,
        };
        addServiceNames(zapierData);
        const sanitizedData = sanitizeZapierData(
          syncableInvoiceUpdateProps,
          zapierData,
        );
        triggerEvent(sanitizedData, "invoiceUpdated", {
          autoSend: mode === "save_send",
        })
          .then(() => {
            setInvoiceSyncPending(data);
            if (mode === "save_send") {
              const historyNote = {
                text: "Invoice sent via external system.",
                parentId: sanitizedData.id,
                parentType: ResourceType.INVOICE,
              };
              logHistory(historyNote);
            }
          })
          .catch((error) => {
            setInvoiceSyncFailed(data);
            logError("invoices:SplitSaveButton:triggerEvent", error);
          });
      }

      notify("Invoice updated successfully", "success");
      redirect(backPath ? backPath : props.basePath);
      deepRedirect(history, backPath);
    } catch (error) {
      setInvoiceSyncFailed(data);
      logError("invoices:InvoiceEdit:onSuccess", error, notify);
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    // A conflict or a void while saving: nothing was written, and the form
    // stays open with the edits so the person can check and save again. The
    // files just uploaded are referenced by nothing; the form still holds
    // them, so the next save uploads them again. After other errors they're
    // left alone: a lost response may mean the write went through.
    if (isUserFacingError(error)) {
      removeFileKeys(capturedRef.current.files?.uploadedKeys);
      notify(error.message, "warning");
      return;
    }
    logError("invoices:InvoiceEdit:onFailure", error, notify);
  };

  const isPristine = () => {
    if (pristine) return true;
    const keys = Object.keys(dirtyFields || {});
    const filtered = keys.filter((k) => !computedFields.has(k));

    const initLineItems = record?.lineItems?.items?.length;
    const currentLineItems = formValues?.lineItems?.items?.length;
    return filtered.length === 0 && initLineItems === currentLineItems;
  };

  if (isSyncEnabled) {
    const modeLabel = mode === "save" ? label : "Save & Send";
    if (isLocked)
      return (
        <Tooltip
          title={
            record?.status === InvoiceStatus.VOID
              ? "Invoice is voided."
              : "Invoice is syncing."
          }
        >
          <span>
            <SaveButton
              label={modeLabel}
              icon={<ImportExportIcon />}
              disabled
            />
          </span>
        </Tooltip>
      );

    return (
      <ButtonGroup>
        <SaveButton
          {...props}
          label={modeLabel}
          icon={isSyncDirty ? <UploadIcon /> : <SaveIcon />}
          submitOnEnter={false}
          undoable={false}
          disabled={isPristine() || isDeleting}
          saving={isSaving}
          color="primary"
          variant="contained"
          transform={transform}
          onSuccess={onSuccess}
          onFailure={onFailure}
        />
        <Button
          color="primary"
          variant="contained"
          disabled={isDeleting}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <ArrowDropDownIcon />
        </Button>
        <Menu
          keepMounted
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              handleModeChange("save");
              setAnchorEl(null);
            }}
          >
            Save
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleModeChange("save_send");
              setAnchorEl(null);
            }}
          >
            Save &amp; Send
          </MenuItem>
        </Menu>
      </ButtonGroup>
    );
  }
  return (
    <SaveButton
      {...props}
      label={label}
      icon={<SaveIcon />}
      submitOnEnter={false}
      undoable={false}
      disabled={isPristine() || isDeleting}
      saving={isSaving}
      transform={transform}
      onSuccess={onSuccess}
      onFailure={onFailure}
    />
  );
};
